
import React, { useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Upload } from "lucide-react";
import { RichTextEditor } from "@/components/RichTextEditor";
import { Constants } from "@/integrations/supabase/types";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/sonner";
import { sanitizeHtml } from "@/utils/sanitization";
import { Database } from "@/integrations/supabase/types";
import { AttachmentsSection } from "../Attachments/AttachmentsSection";

type EntryType = Database["public"]["Enums"]["entry_type"];
type EntryInsert = Database["public"]["Tables"]["entries"]["Insert"];

interface EntryFormProps {
  entry: EntryInsert & { is_pinned?: boolean };
  isNewEntry: boolean;
  userId?: string;
  onFieldChange: (field: string, value: any) => void;
  onContentChange: (content: string) => void;
  markContentChanged: () => void;
}

export const EntryForm: React.FC<EntryFormProps> = ({
  entry,
  isNewEntry,
  userId,
  onFieldChange,
  onContentChange,
  markContentChanged
}) => {
  const entryTypes = Constants.public.Enums.entry_type;

  const handleContentChangeInternal = (newContent: string) => {
    const sanitizedContent = sanitizeHtml(newContent);
    onContentChange(newContent);
    markContentChanged();
  };

  const handleFieldChangeInternal = (field: string, value: any) => {
    onFieldChange(field, value);
    markContentChanged();
  };

  // Ensure that isCreator is calculated and passed to AttachmentsSection
  const isCreator = !!userId && userId === entry.creator_id;

  return (
    <div className="space-y-6">
      <div>
        <Label htmlFor="title">Title</Label>
        <Input
          id="title"
          value={entry.title || ""}
          onChange={(e) => handleFieldChangeInternal("title", e.target.value)}
          placeholder="Enter a title"
          className="max-w-2xl mt-1"
          maxLength={200}
        />
      </div>

      <div>
        <Label htmlFor="type">Category</Label>
        <Select
          value={entry.entry_type}
          onValueChange={(value) => handleFieldChangeInternal("entry_type", value as EntryType)}
          required
        >
          <SelectTrigger className="max-w-xs mt-1">
            <SelectValue placeholder="Select category" />
          </SelectTrigger>
          <SelectContent>
            {entryTypes.map((type) => (
              <SelectItem key={type} value={type}>
                {type}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="mt-2">
          <Badge variant="outline">{entry.entry_type}</Badge>
        </div>
      </div>

      <div>
        <Label htmlFor="content">Content</Label>
        <div className="mt-1">
          <RichTextEditor
            content={entry.content || ''}
            onChange={handleContentChangeInternal}
            entryId={entry.id}
            userId={userId}
          />
        </div>
      </div>

      {/* New Attachments Section */}
      <AttachmentsSection 
        entryId={entry.id}
        userId={userId}
        editable={!isNewEntry && !!entry.id}
        isCreator={isCreator}
      />

    </div>
  );
};

